import { createContext, useContext, useEffect, useState } from 'react';

import { createTheme, ThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { lightColor, darkColor } from './BaseTheme/Colors';
import breakpoints from './BaseTheme/Breakpoint';
import { typographyLight, typographyDark } from './BaseTheme/Typography';
import globals from './BaseTheme/AppBase';

import { linearGradient } from './Helpers';
import { pxToRem } from './Helpers';
import { highchartLightTheme, highchartDarkTheme } from './chartTheme';
import { tableLightTheme, tableDarkTheme } from './tableTheme';

import appbar from './Components/AppBar';
import { lightThemeButton, darkThemeButton } from './Components/Button';
import { lightThemeIconButton, darkThemeIconButton } from './Components/IconButton';

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    appbar: true;
    leftNav: true;
    tocSub: true;
    body2: true;
    body3: true;
    h2: true;
    subtitle1: true;
    subtitle2: true;
    cardFooter: true;
  }
}

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    xxl: true;
  }
}

const lightTheme = createTheme({
  breakpoints: { ...breakpoints },
  //@ts-ignore
  palette: { ...lightColor },
  //@ts-ignore
  typography: {
    ...typographyLight,
    appbar: {
      height: '40px',
    },
  },

  functions: {
    linearGradient,
    pxToRem,
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ...globals,
      },
    },

    MuiCard: {
      styleOverrides: {
        root: ({ ownerState }) => {
          const boxShadow = ownerState.raised
            ? '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)'
            : 'none';
          return {
            backgroundColor: '#fff',
            boxShadow: boxShadow,
          };
        },
      },
    },

    MuiCardHeader: {
      styleOverrides: {
        title: {
          fontSize: '1rem',
          fontWeight: 600,
        },
        root: {
          padding: '0.5rem 1rem',
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '0.5rem 1rem',
        },
      },
    },

    MuiAppBar: { ...appbar },
    MuiIconButton: { ...lightThemeIconButton },
  },
});

const darkTheme = createTheme({
  breakpoints: { ...breakpoints },
  //@ts-ignore
  palette: { ...darkColor },
  //@ts-ignore
  typography: {
    ...typographyDark,
    appbar: {
      height: '40px',
    },
  },

  functions: {
    linearGradient,
    pxToRem,
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ...globals,
      },
    },

    MuiAppBar: { ...appbar },
    MuiIconButton: { ...darkThemeIconButton },
  },
});

const defaultValue = {
  lightTheme: lightTheme,
  darkTheme: darkTheme,
  theme: lightTheme,
  isLightTheme: true,
  currentThemeColor: lightColor,
  currentChartTheme: highchartLightTheme,
  currentTableTheme: tableLightTheme,
};

export const ThemeContext = createContext(defaultValue);

const ThemeContextProvider = ({ children }) => {
  return <ThemeContext.Provider value={defaultValue}>{children}</ThemeContext.Provider>;
};

export default ThemeContextProvider;
